/**
 * A GET of a signed storage URL (files.flashka.ai, or R2) that cannot hang.
 *
 * `fetch` has no timeout of its own. When the storage host does not answer
 * (files-dev was seen stalling in the TLS handshake on a match evening, and a
 * blocked host behaves the same), a viewer awaiting the bytes spins forever.
 * Here each attempt gets {@link STORAGE_RESPONSE_TIMEOUT_MS} to answer with
 * its headers; one that does not is aborted and asked once more, then fails
 * with {@link StorageReadTimeoutError}, which callers show as their existing
 * load error.
 *
 * Only the wait for the answer is bounded, never the body: a big PDF on a slow
 * connection may take minutes to arrive, and that is not a hang.
 *
 * An HTTP answer, whatever its status, is returned as it is: a 403 or 404 is
 * storage speaking, and asking again would get the same answer.
 */

/** How long one attempt waits for the storage host to answer. */
export const STORAGE_RESPONSE_TIMEOUT_MS = 20_000;

/** Attempts in all: the first, and one more after a timeout or a lost connection. */
export const STORAGE_READ_ATTEMPTS = 2;

/** The storage host did not answer in time, twice. */
export class StorageReadTimeoutError extends Error {
  constructor(readonly timeoutMs: number) {
    super(`Storage did not answer within ${timeoutMs} ms`);
    this.name = 'StorageReadTimeoutError';
  }
}

export interface FetchStoredOptions {
  /** Defaults to the global `fetch`. */
  fetch?: typeof fetch;
  timeoutMs?: number;
  attempts?: number;
}

export async function fetchStored(
  url: string,
  init: RequestInit = {},
  {
    fetch: request = (input, i) => fetch(input, i),
    timeoutMs = STORAGE_RESPONSE_TIMEOUT_MS,
    attempts = STORAGE_READ_ATTEMPTS,
  }: FetchStoredOptions = {},
): Promise<Response> {
  const outer = init.signal ?? undefined;
  for (let attempt = 1; ; attempt++) {
    const controller = new AbortController();
    const forward = () => controller.abort();
    outer?.addEventListener('abort', forward, { once: true });
    if (outer?.aborted) controller.abort();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    try {
      // The caller's signal keeps reaching the body once the answer is in.
      return await request(url, { ...init, signal: controller.signal });
    } catch (error) {
      outer?.removeEventListener('abort', forward);
      // The caller gave up: that is theirs to handle, not a reason to retry.
      if (outer?.aborted) throw error;
      if (attempt >= attempts) {
        throw timedOut ? new StorageReadTimeoutError(timeoutMs) : error;
      }
    } finally {
      clearTimeout(timer);
    }
  }
}
